/**
 * How to render `queue_summary.carryover`.
 *
 * Restored from the pre-`858e182` workspace (its contract for this field is
 * unchanged) and re-pointed at the workspace types. The distinction it encodes
 * is the one that is easy to get wrong: a timezone FALLBACK is a success —
 * carryover was computed against a fixed six-hour UTC block because the location
 * has no configured timezone — while a null count is a real configuration error.
 * Collapsing the two would either hide a misconfiguration or report a working
 * queue as broken.
 */
import i18n from "@/shared/lib/i18n/index.ts";
import type { AlertQueueCarryover } from "@/alerts/types/alerts-workspace";

export interface CarryoverDisplay {
  /** Formatted count; "—" when unavailable. Null is NEVER rendered as 0. */
  value: string;
  /** Short block-context suffix, e.g. "UTC block" or the configured timezone. */
  context: string | null;
  /** True only for a real config error (count === null). */
  unavailable: boolean;
  /** Backend-provided reason, surfaced when unavailable. */
  reason: string | null;
}

export function describeCarryover(carryover: AlertQueueCarryover): CarryoverDisplay {
  if (carryover.count === null) {
    return {
      value: "—",
      context: null,
      unavailable: true,
      reason: carryover.unavailable_reason,
    };
  }

  const context = carryover.timezone_fallback
    ? i18n.t("queue.summary.utcBlock", { ns: "alerts" })
    : (carryover.timezone ?? null);

  return { value: String(carryover.count), context, unavailable: false, reason: null };
}
